import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { companyBankAccountSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import * as companiesActionCreators from '../companies/actionCreators'
import * as currenciesActionCreators from '../currencies/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { CompanyBankAccount } from './model'
import { Company } from '../companies/model'
import { Currency } from '../currencies/model'

// Fetch

const fetchCompanyBankAccountsEpic = (action$, store) => {
    const tableIdentifier = 'company_bank_accounts_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `company-bank-accounts/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(companyBankAccountSchema)
                        )

                        const companyBankAccounts = normalizedEntitiesToRecordMap(
                            normalizedData.entities.company_bank_accounts,
                            CompanyBankAccount,
                            normalizedData.result
                        )
                        const companies = normalizedEntitiesToRecordMap(normalizedData.entities.companies, Company)
                        const currencies = normalizedEntitiesToRecordMap(normalizedData.entities.currencies, Currency)

                        const observables = [
                            Observable.of(actionCreators.fetchCompanyBankAccountsFulfilled(companyBankAccounts)),
                            Observable.of(companiesActionCreators.fetchCompaniesFulfilled(companies)),
                            Observable.of(currenciesActionCreators.fetchCurrenciesFulfilled(currencies)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchCompanyBankAccountFulfilled(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchCompanyBankAccountEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().companies.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `company-bank-accounts/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, companyBankAccountSchema)

                        const companyBankAccounts = normalizedEntitiesToRecordMap(normalizedData.entities.company_bank_accounts, CompanyBankAccount)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchCompanyBankAccountsFulfilled(companyBankAccounts)),
                            Observable.of(actionCreators.fetchCompanyBankAccountFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchCompanyBankAccountRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveCompanyBankAccountEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().companies.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload)

            let path = `company-bank-accounts`
            let method = 'POST'

            if (values.id) {
                path = `company-bank-accounts/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, companyBankAccountSchema)

                        const companyBankAccounts = normalizedEntitiesToRecordMap(normalizedData.entities.company_bank_accounts, CompanyBankAccount)
                        const companyBankAccount = companyBankAccounts.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveCompanyBankAccountFulfilled(companyBankAccount)))
                    })
                    .catch(error => Observable.of(actionCreators.saveCompanyBankAccountRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteCompanyBankAccountEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().companies.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `company-bank-accounts/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteCompanyBankAccountFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteCompanyBankAccountRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Export

const exportCompanyBankAccountsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `company-bank-accounts/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

export default [
    fetchCompanyBankAccountsEpic,
    fetchCompanyBankAccountEpic,
    saveCompanyBankAccountEpic,
    deleteCompanyBankAccountEpic,
    exportCompanyBankAccountsEpic,
]
